// @flow

import React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import t from 'app/utils/translate';
import { map, some, debounce, sortBy, noop } from 'lodash';
import { connect } from 'react-redux';
import { viewFriend } from 'app/actions/friendsActions';
import { createFriendRequests } from 'app/actions/friendRequestActions';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import GroupContainer from 'app/components/friends/GroupContainer';
import FriendsCardList from 'app/components/friends/FriendsCardList';
import FriendCard from 'app/components/friends/friendCard/FriendCard';
import { THEY_REQUESTED } from 'app/constants/friendStatusTypes';
import { FRIEND_GROUP_ID } from 'app/constants/friendConstants';
import GroupContainerCategories from 'app/components/friends/GroupContainerCategories';
import GroupContainerCategoryItem from 'app/components/friends/GroupContainerCategoryItem';
import Icon from 'app/components/commons/Icon';
import { searchUsers, clearResults } from 'app/actions/friendSearchActions';
import GroupContainerHeader from 'app/components/friends/GroupContainerHeader';
import GroupContainerSearch from 'app/components/friends/GroupContainerSearch';
import GroupContentsSearchResults from 'app/components/friends/GroupContentsSearchResults';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';

type Props = {
  classes: Object,
  friends?: { [id: number]: Friend },
  requests?: { [id: number]: FriendRequest },
  isMobile?: boolean,
  onSearchStart?: () => void,
  onSearchEnd?: () => void,
  onSearchUsers: (term: string) => void,
  onClearSearchResults: (searchId: number | string) => void,
  onViewFriend: (id: string | number) => void,
  onCreateFriendRequest: () => void,
  hasInitialised?: boolean,
  className?: string,
};

type State = {
  selectedUser: iFriendUser,
  activeCategory: Object,
  searchTerm: string,
  searchUser: iFriendUser,
  isSearching: boolean,
};

// object descriptors for all the categories available
// for this 'fake group' group container
const categories = {
  friends: {
    id: 1,
    name: t('Friends'),
    icon: 'icon-friend',
    isDisabled: () => false,
  },
  pending: {
    id: 2,
    name: t('Pending'),
    icon: 'icon-pending',
    isDisabled: (props: Props) => props.requests.length === 0,
  },
};

// object descriptors for all the lets get started helpers
const searchForFriends = {
  id: 1,
  icon: 'icon-search',
  text: t('search for friends'),
  color: 'primary',
};

class GroupContentsMyFriends extends React.Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedUser: undefined,
      activeCategory: categories.friends,
      searchTerm: '',
    };
    
    this.triggerSearch = debounce((term) => {
      this.setState({ searchTerm: term });
      this.props.onSearchUsers(term, FRIEND_GROUP_ID);
    }, props.isMobile ? 700 : 400);
  }

  static defaultProps = {
    friends: {},
    requests: {},
    searchResults: [],
    isMobile: true,
    isSearching: false,
    onSearchEnd: noop,
    onSearchStart: noop,
    hasInitialised: false,
    className: undefined,
  };

  setCategory = (category) => {
    this.setState({ activeCategory: category });
  };

  messageItemClick = (item) => {
    if (item === searchForFriends) {
      this.setState({ isSearching: true });
    }
  };

  toggleUser = (user: iFriendUser) => {
    const { onViewFriend } = this.props;
    const { selectedUser } = this.state;
    onViewFriend(user);

    if (selectedUser && selectedUser.id === user.id) {
      this.setState({ selectedUser: undefined });
    } else {
      this.setState({ selectedUser: user });
    }
  };

  onSearchChange = (terms: string) => {
    if (terms) {
      this.triggerSearch(terms);
    } else {
      // if they cleared it, set it straitht away
      this.setState({ searchTerm: terms });
      this.props.onClearSearchResults(FRIEND_GROUP_ID);
    }
  };

  onSearchStart = (...args) => {
    this.setState({ isSearching: true });
    this.props.onSearchStart(...args);
  };

  onSearchEnd = (...args) => {
    this.setState({ isSearching: false });
    this.props.onSearchEnd(...args);
  };

  buildCards(userList: Array<iFriendUser>) {
    const { classes } = this.props;

    return sortBy(userList, 'status')
      .reverse()
      .map(user => (
        <FriendCard friend={user} onClick={() => this.toggleUser(user)} key={user.id}>
          {user.status === THEY_REQUESTED && (
            <div className={classNames(classes.requestedDot, 'absolute')} />
          )}
        </FriendCard>
      ));
  }

  render() {
    const {
      classes,
      friends,
      requests,
      hasInitialised,
      onCreateFriendRequest,
      className,
    } = this.props;
    const { activeCategory, searchTerm, selectedUser } = this.state;

    const hasRequests = some(requests);
    const userList = Object.values(activeCategory === categories.friends ? friends : requests);
    const hasAnyIncomingRequests = some(requests, { status: THEY_REQUESTED });

    return (
      <GroupContainer className={classNames(className, classes.myFriendsContainer)}>
        <GroupContainerHeader>
          <div className={classes.title}>{t('My Friends')}</div>
          <GroupContainerSearch
            isSearching={this.state.isSearching}
            onSearchChange={this.onSearchChange}
            onSearchStart={this.onSearchStart}
            onSearchEnd={this.onSearchEnd}
            searchTerm={searchTerm}
          />
        </GroupContainerHeader>
        <AnimatedGroup
          className={classNames(classes.innerContainer, { isSearching: !!searchTerm })}
          onEnter={Enter.appear(0.3, 0.3)}
          onLeave={Leave.disappear(0.3)}
        >
          {searchTerm && (
            <GroupContentsSearchResults
              searchId={FRIEND_GROUP_ID}
              searchTerm={searchTerm}
              onCreateFriendRequest={onCreateFriendRequest}
              members={map(friends, 'id').concat(map(requests, 'id'))}
              isNew
              key="search"
              className={classes.search}
            />
          )}
          {!searchTerm && (
            <GroupContainerCategories key="categories">
              {map(categories, (cat) => {
                const isActive = activeCategory === cat;
                return (
                  <GroupContainerCategoryItem
                    isActive={isActive}
                    onClick={() => this.setCategory(cat)}
                    key={cat.id}
                    disabled={cat.isDisabled(this.props)}
                  >
                    <Icon type={cat.icon} />
                    <span>{cat.name}</span>
                    {hasAnyIncomingRequests &&
                      cat === categories.pending && <div className={classes.requestedDot} />}
                  </GroupContainerCategoryItem>
                );
              })}
            </GroupContainerCategories>
          )}
          {!searchTerm &&
            hasInitialised && (
              <React.Fragment>
                {userList.length > 0 ? (
                  <FriendsCardList selectedUserId={(selectedUser || {}).id} key="friends">
                    {this.buildCards(userList)}
                  </FriendsCardList>
                ) : (
                  <React.Fragment>
                    {activeCategory === categories.friends && (
                      <React.Fragment>
                        {hasRequests ? (
                          <div className="text-center">
                            Hold tight. You have pending friend requests.
                            <p>
                              Once they accept your invitation you will be able to follow them on
                              the leaderboard.
                            </p>
                          </div>
                        ) : (
                          <div className="text-center">
                            Always see your friends on the leaderboard.
                            <p>Let's get started, use the search bar above to find your friends.</p>
                          </div>
                        )}
                      </React.Fragment>
                    )}
                    {activeCategory === categories.pending && <div>No pending requests</div>}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
        </AnimatedGroup>
      </GroupContainer>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    search: {
      margin: [-15, -10],
    },
    myFriendsContainer: {
      flexGrow: 1,
      position: 'relative',
    },
    innerContainer: {
      padding: [15, 10],
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      '& > *:not(:last-child)': {
        marginBottom: 15,
      },
    },
    requestedDot: {
      width: 8,
      height: 8,
      backgroundColor: theme.color.success,
      borderRadius: '100%',
      alignSelf: 'flex-start',
      '&.absolute': {
        position: 'absolute',
        top: 8,
        right: 8,
      },
    },
    title: {
      textTransform: 'uppercase',
      color: theme.color.basis2,
      fontSize: 18,
      fontWeight: 600,
      textAlign: 'center',
    },
  };
}

function mapStateToProps(state: GlobalState) {
  return {
    friends: state.friends.friends,
    requests: state.friendRequests.friendRequests,
    hasInitialised: state.friends.hasInitialised && state.friendRequests.hasInitialised,
  };
}

const actions = {
  onSearchUsers: searchUsers,
  onViewFriend: viewFriend,
  onCreateFriendRequest: createFriendRequests,
  onClearSearchResults: clearResults,
};

export default connect(mapStateToProps, actions)(useSheet(applyStyles)(GroupContentsMyFriends));
